import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  ConfirmResult,
  editFormDataAF,
  EditFormMode,
  hookFormContextAF,
  IRideDto,
  rideConvertToDynamic,
  RideCrudModeKey,
  SimpleResult,
} from "@ryde/shared";

import { useCrudEditContext } from "../../../contexts";
import { useModalConfirm } from "../../../hooks-singletons";

export const useFixedRidesActions = () => {
  const { t } = useTranslation();
  const { entity, mode } = useCrudEditContext<IRideDto>();
  const [rideDtoGlobal, setRideDtoGlobal] = useRecoilState<
    Partial<IRideDto> | undefined
  >(editFormDataAF([entity, mode || EditFormMode.CrudRoute]));
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );
  const { confirmDialogResult } = useModalConfirm();

  const dynamicConvertEditRideConfirmed = useEvent(
    async (): Promise<SimpleResult<Partial<IRideDto> | undefined>> => {
      if (!rideDtoGlobal?.courseId) {
        return new SimpleResult({ error: t("ride.RIDE_IS_ALREADY_DYNAMIC") });
      }

      const dynamicConversionActionConfirmed = await confirmDialogResult({
        title: t("ride.DYNAMIC_CONVERSION_CONFIRM_TITLE"),
        message: t("ride.DYNAMIC_CONVERSION_CONFIRM_MESSAGE"),
      });

      if (dynamicConversionActionConfirmed !== ConfirmResult.OK) {
        return new SimpleResult({
          error: "general.RIDES_ACTIONS_WARNINGS.DYNAMIC_CONVERSION_CANCELED",
        });
      }

      const convertedRide = rideConvertToDynamic(rideDtoGlobal);

      setRideDtoGlobal(convertedRide);
      rideFormContext?.setValue("courseId", convertedRide?.courseId ?? null, {
        shouldDirty: true,
      });

      // Caller (e.g. add passenger) uses data for dry-save so payload matches form state.
      return new SimpleResult({ success: true, data: convertedRide });
    },
  );

  return { dynamicConvertEditRideConfirmed };
};
